import type {
  IPolicy,
  IPolicyBulkUpdateForm,
  IPolicyFilters,
  IPolicyGroup,
  IPolicyGroupCreationForm,
  IPolicyGroupCreationResponse,
  IPolicyGroupUpdateForm,
} from '@/types/asicw/policies'
import type { IPolicyGroupFilters } from '@/types/asicw/policy-groups'
import type { ILiteSiteFilters } from '@/types/core/filters'
import type { LiteSite } from '@/types/core/Site'
import type { IPage } from '@/types/shared/pagination'
import axios from 'axios'

export async function createPolicyGroup(form: IPolicyGroupCreationForm): Promise<IPolicyGroupCreationResponse> {
  // Assert mandatory fields
  if (
    !form.group.groupNumber ||
    !form.group.creationDate ||
    !form.group.serviceAgreement?.raw ||
    !form.details.location ||
    !form.group.customerID
  )
    throw new Error('Call to createPolicyGroup is missing required params')

  // Translate to form-data
  const formData = new FormData()
  formData.append('group_number', form.group.groupNumber)
  formData.append('customer_id', form.group.customerID.toString())
  formData.append('effective_date', form.group.creationDate.toISOString())
  formData.append('initial_term_months', form.group.initialTerm.toString())
  formData.append('subsequent_term_months', form.group.subsequentTerm.toString())
  formData.append('signed_document', form.group.serviceAgreement.raw)
  formData.append('site_id', form.details.location.site_id!)
  if (form.details.addByCSV && form.details.minersCSV) {
    formData.append('miners_csv', form.details.minersCSV.raw as Blob)
  } else {
    for (const idx in form.miners) {
      const miner = form.miners[idx]
      formData.append(`miners[${idx}]sn`, miner.sn)
      formData.append(`miners[${idx}]model_family`, miner.model_family ?? '')
      formData.append(`miners[${idx}]environment`, miner.environment)
      formData.append(`miners[${idx}]cooling_method`, miner.coolingMethod)
      formData.append(`miners[${idx}]third_party_firmware_used`, miner.running3rdPartySoft.toString())
      formData.append(`miners[${idx}]overclocking`, miner.overclockingMiners.toString())
      if (miner.policySalePrice) {
        formData.append(`miners[${idx}]sale_price`, miner.policySalePrice.toString())
      }
    }
  }

  // Post
  const res = await axios.post(import.meta.env.VITE_APP_ASICW_POLICY_GROUPS_ENDPOINT, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export async function getPolicyGroups(filters: IPolicyGroupFilters): Promise<IPage<IPolicyGroup>> {
  const res = await axios.get(import.meta.env.VITE_APP_ASICW_POLICY_GROUPS_ENDPOINT, { params: filters })
  return res.data
}

export async function getPolicies(filters: IPolicyFilters): Promise<IPage<IPolicy>> {
  const res = await axios.get(import.meta.env.VITE_APP_ASICW_POLICIES_ENDPOINT, { params: filters })
  return res.data
}

export async function bulkUpdatePolicies(form: IPolicyBulkUpdateForm): Promise<void> {
  const res = await axios.patch(`${import.meta.env.VITE_APP_ASICW_POLICIES_ENDPOINT}/bulk`, form)
  return res.data
}

/**
 * Retrieves the list of sites available to declare as policy location
 * @param filters
 * @returns the list of sites
 */
export async function getSitesOptions(filters?: ILiteSiteFilters): Promise<IPage<LiteSite>> {
  const res = await axios.get(`${import.meta.env.VITE_APP_ASICW_API}/policy-sites`, { params: filters })
  return res.data
}

export async function downloadSampleBulkPolicyMinersFile(): Promise<Blob> {
  const res = await axios.get(`${import.meta.env.VITE_APP_ASICW_API}/policy-group-miners-sample`, {
    responseType: 'blob',
  })
  return res.data
}

export async function getPoliciesExportCSV(filters: IPolicyFilters): Promise<Blob> {
  const res = await axios.get(`${import.meta.env.VITE_APP_ASICW_POLICIES_ENDPOINT}/export`, {
    params: filters,
    responseType: 'blob',
  })
  return res.data
}

export async function updatePolicyGroup(id: number, form: IPolicyGroupUpdateForm): Promise<any> {
  const formData = new FormData()

  formData.append('effective_date', form.effective_date.toISOString())
  formData.append('initial_term_months', form.initial_term_months.toString())
  formData.append('subsequent_term_months', form.subsequent_term_months.toString())
  formData.append('status', form.status)
  if (form.new_signed_document?.raw) {
    formData.append('new_signed_document', form.new_signed_document.raw)
  }

  const res = await axios.patch(`${import.meta.env.VITE_APP_ASICW_POLICY_GROUPS_ENDPOINT}/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}
